// Groups them in Sentry, and exempts them from sampling (see logging)
export const UNEXPECTED_RESPONSE_FINGERPRINT = 'unexpected-api-response';

// Enough to show what answered instead of us: an interstitial usually names
// the product in its <title> or first lines
const SNIPPET_LENGTH = 400;

const toSnippet = (body: string) =>
  body.replace(/\s+/g, ' ').trim().slice(0, SNIPPET_LENGTH);

// Further than the snippet, as a long <head> can push the <title> past it
const TITLE_SEARCH_LENGTH = 5_000;
const TITLE_LENGTH = 200;

const toTitle = (body: string) =>
  body
    .slice(0, TITLE_SEARCH_LENGTH)
    .match(/<title[^>]*>([^<]*)<\/title>/i)?.[1]
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, TITLE_LENGTH) || undefined;

type Details = {
  url?: string;
  status?: number;
  contentType?: string;
  body: string;
};

/**
 * A response that arrived but was not the JSON asked for: something on the
 * network path (VPN, web filter, proxy, HTTPS inspection, a challenge page)
 * answered in place of the API. Still a SyntaxError, as that is what it used
 * to surface as.
 */
export class UnexpectedResponseError extends SyntaxError {
  url?: string;

  status?: number;

  contentType?: string;

  // For the user to see, and send to us if they want: may hold their details
  snippet: string;

  // Names what answered, without the rest of the page: safe to log
  title?: string;

  constructor(message: string, { url, status, contentType, body }: Details) {
    super(message);
    this.name = 'UnexpectedResponseError';
    this.url = url;
    this.status = status;
    this.contentType = contentType;
    this.snippet = toSnippet(body);
    this.title = toTitle(body);
  }
}

/** What the browser knows at the moment of failure, as lines of text */
export const unexpectedResponseDiagnostics = (error?: unknown) => {
  const lines = [
    `Page URL: ${globalThis.location?.href}`,
    `Online: ${typeof navigator === 'undefined' ? 'unknown' : navigator.onLine}`,
  ];
  if (error instanceof UnexpectedResponseError) {
    lines.push(
      `API URL: ${error.url || 'unknown'}`,
      `HTTP status: ${error.status ?? 'none'}`,
      `Content type: ${error.contentType || 'none'}`
    );
  }
  if (error instanceof Error) {
    lines.push(`Error message: ${error.message}`);
  }
  if (error instanceof UnexpectedResponseError) {
    lines.push(`Response received instead of JSON:\n${error.snippet}`);
  }
  return lines.join('\n');
};
